import { Box, styled } from '@mui/material'
import { useAppSelector } from 'app/state'
import { CommonPrivacy } from 'features/common'
import { Group } from 'features/groups'
import { FormattedMessage } from 'react-intl'
import { RoundedIconProps } from '../../RoundedIcon'
import { ShareOptionLabelProps } from '../ShareOptionLabel'
import PrivacyIcon from '../PrivacyIcon'
import { ReactNode } from 'react'
import EllipsisWithTooltip from '../../utils/EllipsisWithTooltip'
import useAccountQueries from 'features/account/api/accountQueries'
import { useQuery } from 'react-query'

const AccountName = styled('span')(({ theme }) => ({
  color: theme.palette.text.primary
}))

const NoWrapBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(0.5),

  overflow: 'hidden',
  whiteSpace: 'nowrap'
}))

export function SharedOptionDescription({ extraText, groups }: { extraText?: ReactNode; groups?: Group[] }) {
  const { queries } = useAccountQueries()
  const { data: users } = useQuery({ ...queries.getUsers })
  const accountName = useAppSelector((state) => state.users.me?.user_metadata.account_name)

  const account = (
    <EllipsisWithTooltip>
      <AccountName>{accountName}</AccountName>
    </EllipsisWithTooltip>
  )

  if (groups?.length) {
    const numGroups = groups.length
    return (
      <NoWrapBox>
        <FormattedMessage
          id="common.shareDialog.shareWithGroups.description"
          values={{
            account,
            groups: (
              <span>
                <FormattedMessage
                  id="common.shareDialog.shareWithGroups.descriptionGroups"
                  values={{ groups: numGroups }}
                />
              </span>
            )
          }}
        />
        {extraText}
      </NoWrapBox>
    )
  }
  return (
    <NoWrapBox>
      <FormattedMessage
        id="common.shareDialog.sharedDesc"
        values={{
          account,
          num: users?.length
        }}
      />
      {extraText}
    </NoWrapBox>
  )
}

function PrivacyIconShared(props: Omit<RoundedIconProps, 'icon'>) {
  return <PrivacyIcon privacy={CommonPrivacy.Shared} {...props} />
}

export const sharedOptionDescriptor: ShareOptionLabelProps = {
  title: <FormattedMessage id="common.shareDialog.sharedOption" />,
  description: <SharedOptionDescription />,
  iconComponent: PrivacyIconShared
}

export const sharedWithAllOptionDescriptor: ShareOptionLabelProps = {
  title: <FormattedMessage id="common.shareDialog.shareWithAll.option" />,
  description: <SharedOptionDescription />,
  iconComponent: PrivacyIconShared
}

export function getSharedWithGroupsOptionDescriptor(groups: Group[]): ShareOptionLabelProps {
  return {
    title: <FormattedMessage id="common.shareDialog.shareWithGroups.option" values={{ groups: groups?.length ?? 0 }} />,
    description: <SharedOptionDescription groups={groups} />,
    iconComponent: PrivacyIconShared
  }
}
